import { useMemo } from "react";

import { Box, CircularProgress, Stack } from "@mui/material";
import PubNub from "pubnub";
import { PubNubProvider } from "pubnub-react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { SignUpContainer } from "components/Authentication/components/SignUp/SignUpContainer";
import { CarrierEssentialsUpgradeMenu } from "components/Carrier/components/CarrierEssentialsUpgradeMenu";
import { ChatPage } from "components/ChatPage";
import { ChatRealtimeListener } from "components/ChatRealtimeListener";
import { CompanyBranchesPage } from "components/CompanyBranchesPage";
import { CompanyDetails } from "components/CompanyDetails";
import { CompanyProfilePage } from "components/CompanyProfile";
import { CustomerIOWrapper } from "components/CustomerIOWrapper";
import { FeaturebaseWrapper } from "components/FeaturebaseWrapper";
import { Network } from "components/Network";
import { NotFoundPage } from "components/NotFoundPage";
import { DataPageWrapper } from "components/Shipper/components/DataPageWrapper";
import { ShipmentTrackingDashboardPage } from "components/Shipper/components/ShipmentTrackingDashboardPage";
import { ShipperUpgradePage } from "components/Shipper/components/ShipperUpgradePage";
import { SupplierShipmentContainer } from "components/Shipper/components/SupplierShipments/SupplierShipmentContainer";
import { SupplierNetwork } from "components/SupplierNetwork/SupplierNetwork";
import { UserAccountPage } from "components/UserAccountPage";
import { userBelongsToCompany } from "utils";

import { Authentication } from "./components/Authentication";
import { ExternalInviteSignUpContainer } from "./components/Authentication/components/ExternalInviteSignUp";
import { CommandLinkLanding } from "./components/CommandLinkLanding";
import { CompanySetup } from "./components/CompanySetup";
import { ContentSwitcher } from "./components/ContentSwitcher";
import { MicroReviewContainer } from "./components/MicroReview/MicroReviewContainer";
import { PublicShipmentPage } from "./components/PublicShipment";
import { PublicShipmentMarketingHeader } from "./components/PublicShipment/components/PublicShipmentMarketingHeader";
import { Shipment } from "./components/Shipment";
import {
  AUTH_ONLY_RELATIVE_URLS,
  CARRIER_UPGRADE_RELATIVE_URL,
  COMMAND_LINK_LANDING_RELATIVE_URL,
  SHIPPER_UPGRADE_RELATIVE_URL,
} from "./constants";
import {
  CarrierQuoteFiltersProvider,
  ChatUnreadProvider,
  RoleProvider,
  ShipmentFiltersProvider,
  SubscriptionProvider,
} from "./context";
import { NotificationProvider } from "./context/NotificationContext";
import { useAuthContext, useLoadPath } from "./hooks";
import { AppLayout } from "./layouts";
import { PageLayout } from "./layouts/PageLayout";

function App() {
  const {
    isAuthenticated,
    user,
    challenge,
    pubnub: pubnubUserInfo,
    isInitialized,
  } = useAuthContext();
  useLoadPath();
  const location = useLocation();
  const isPublicRoute = location.pathname.startsWith("/public/");
  const userHasCompany = userBelongsToCompany(user);

  const showLogin = isInitialized && (!isAuthenticated || user == null);
  const showForceChangePassword =
    challenge?.ChallengeName === "NEW_PASSWORD_REQUIRED";

  // Memoize the PubNub client so re-renders of App don't replace the instance
  // — replacing it would tear down every subscription effect downstream and
  // abort the underlying long-poll request.
  // pubnubUserInfo?.token is intentionally excluded from deps: the token is
  // refreshed imperatively via setToken (in useChatPublishToken) and must not
  // cause a new instance to be constructed. Recreating it on every chat token
  // refresh tears down every subscription and triggers live-update effects to
  // fire an unwanted silent revalidation, creating an infinite refresh loop.
  const pubnub = useMemo(
    () =>
      new PubNub({
        publishKey: pubnubUserInfo?.publish_key ?? "unknown",
        subscribeKey: pubnubUserInfo?.subscribe_key ?? "unknown",
        userId: pubnubUserInfo?.user_uuid ?? "unknown",
        authKey: pubnubUserInfo?.token ?? "unknown",
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pubnubUserInfo?.publish_key,
      pubnubUserInfo?.subscribe_key,
      pubnubUserInfo?.user_uuid,
    ],
  );

  if (isPublicRoute) {
    return (
      <>
        <PublicShipmentMarketingHeader />
        <PageLayout>
          <Routes>
            <Route
              path="/public/shipments/:token"
              element={<PublicShipmentPage />}
            />
          </Routes>
        </PageLayout>
      </>
    );
  }

  if (!isInitialized) {
    return (
      <Box
        minHeight="100vh"
        minWidth="100vw"
        bgcolor="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {showLogin || showForceChangePassword ? (
        <Authentication />
      ) : (
        <RoleProvider>
          <SubscriptionProvider>
            <ShipmentFiltersProvider>
              <CarrierQuoteFiltersProvider>
                <NotificationProvider>
                  <PubNubProvider client={pubnub}>
                    <ChatUnreadProvider>
                      <ChatRealtimeListener />
                      <AppLayout
                        hideNav={!userHasCompany}
                        content={
                          <>
                            <CustomerIOWrapper />
                            <FeaturebaseWrapper />
                            <Routes>
                              <Route path="/" element={<ContentSwitcher />} />
                              <Route
                                path="/sign-up"
                                element={<SignUpContainer />}
                              />
                              <Route
                                path="/external-network-sign-up"
                                element={
                                  <ExternalInviteSignUpContainer signUpRole="CARRIER" />
                                }
                              />
                              <Route
                                path="/carrier-sign-up"
                                element={
                                  <ExternalInviteSignUpContainer signUpRole="CARRIER" />
                                }
                              />
                              <Route
                                path="/shipper-sign-up"
                                element={
                                  <ExternalInviteSignUpContainer signUpRole="SHIPPER" />
                                }
                              />
                              <Route path="/chat" element={<ChatPage />} />
                              <Route path="/chat/:id" element={<ChatPage />} />
                              <Route
                                path="/shipments/:id"
                                element={<Shipment />}
                              />
                              <Route
                                path="/supplier-shipments/:id"
                                element={<SupplierShipmentContainer />}
                              />
                              <Route
                                path="/setup-company"
                                element={<CompanySetup />}
                              />
                              <Route
                                path="/companies/:id"
                                element={<CompanyDetails />}
                              />
                              <Route
                                path="/companies/:id/profile"
                                element={<CompanyProfilePage />}
                              />
                              <Route
                                path="/companies/:id/branches"
                                element={<CompanyBranchesPage />}
                              />
                              <Route
                                path="/account"
                                element={<UserAccountPage />}
                              />
                              <Route path="/network" element={<Network />} />
                              <Route
                                path="/supplier-network"
                                element={<SupplierNetwork />}
                              />
                              <Route
                                path={CARRIER_UPGRADE_RELATIVE_URL}
                                element={<CarrierEssentialsUpgradeMenu />}
                              />
                              <Route
                                path={SHIPPER_UPGRADE_RELATIVE_URL}
                                element={<ShipperUpgradePage />}
                              />
                              <Route
                                path="/shippers/data"
                                element={<DataPageWrapper />}
                              />
                              <Route
                                path="/shippers/data/shipment-tracking"
                                element={<ShipmentTrackingDashboardPage />}
                              />
                              <Route
                                path="/submit-shipment-micro-review"
                                element={<MicroReviewContainer />}
                              />
                              <Route
                                path={COMMAND_LINK_LANDING_RELATIVE_URL}
                                element={<CommandLinkLanding />}
                              />
                              {AUTH_ONLY_RELATIVE_URLS.map((path) => (
                                <Route
                                  key={path}
                                  path={path}
                                  element={<Navigate to="/" replace />}
                                />
                              ))}
                              <Route path="*" element={<NotFoundPage />} />
                              {/*
                            DO NOT ADD ANY ROUTE BELOW THIS POINT.
                            ROUTES AFTER "*" IS UNREACHABLE
                          */}
                            </Routes>
                          </>
                        }
                      />
                    </ChatUnreadProvider>
                  </PubNubProvider>
                </NotificationProvider>
              </CarrierQuoteFiltersProvider>
            </ShipmentFiltersProvider>
          </SubscriptionProvider>
        </RoleProvider>
      )}
    </Box>
  );
}

export default App;
